import type { AdminUserDetailDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { observer } from 'mobx-react-lite';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AppealsTab } from './_components/AppealsTab';
import { OrdersTab } from './_components/OrdersTab';
import { ProfileTab } from './_components/ProfileTab';
import { ReactivateUserDialog } from './_components/ReactivateUserDialog';
import { SuspendUserDialog } from './_components/SuspendUserDialog';
import { UserDetailFooter } from './_components/UserDetailFooter';
import { UserDetailHeader } from './_components/UserDetailHeader';
import { useUserStatusActions } from './_lib/useUserStatusActions';
import { useAdminUserDetail } from '../../api/admin-users';
import { EntityAuditTrail } from '../../components/audit';

/**
 * User detail drawer (AUT-124 / ADMIN-12).
 *
 * Right-side 720px drawer with tabs Профиль / Заказы / Апелляции / Журнал
 * + a footer with Suspend / Reactivate (`useUserStatusActions`; role +
 * permission + audit gated server-side). On success the `['admin','users']`
 * cache prefix is invalidated so the list + this drawer refetch.
 *
 * Opens when `userId` is non-null; the detail query is `enabled` only
 * then, so closing it stops the fetch.
 */
const DRAWER_WIDTH = 720;

type TabKey = 'profile' | 'orders' | 'appeals' | 'log';

interface UserDetailDrawerProps {
  userId: string | null;
  onClose: () => void;
}

const DrawerBody = observer(
  ({ user, onClose }: { user: AdminUserDetailDto; onClose: () => void }): ReactElement => {
    const { t } = useTranslation();
    const [tab, setTab] = useState<TabKey>('profile');
    const [suspendOpen, setSuspendOpen] = useState(false);
    const [reactivateOpen, setReactivateOpen] = useState(false);
    const { busy, run } = useUserStatusActions();

    const isSuspended = user.status === 'suspended';

    const onConfirm = (next: 'suspended' | 'active', reason: string): void => {
      void run(user.id, next, reason).then((ok) => {
        if (ok) {
          setSuspendOpen(false);
          setReactivateOpen(false);
        }
      });
    };

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: { xs: '100vw', sm: DRAWER_WIDTH },
            maxWidth: '100vw',
          }}
        >
          <UserDetailHeader user={user} onClose={onClose} />

          <Tabs
            value={tab}
            onChange={(_e, v: TabKey) => setTab(v)}
            sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider', minHeight: 44 }}
          >
            <Tab value="profile" label={t('users.detail.tabs.profile')} />
            <Tab
              value="orders"
              label={`${t('users.detail.tabs.orders')} (${user.ordersSummary.total})`}
            />
            <Tab
              value="appeals"
              label={`${t('users.detail.tabs.appeals')} (${user.appealsFiled})`}
            />
            <Tab value="log" label={t('users.detail.tabs.log')} />
          </Tabs>

          <Box sx={{ flex: 1, overflow: 'auto', p: '22px 24px' }}>
            {tab === 'profile' ? <ProfileTab user={user} /> : null}
            {tab === 'orders' ? <OrdersTab user={user} /> : null}
            {tab === 'appeals' ? <AppealsTab user={user} /> : null}
            {tab === 'log' ? <EntityAuditTrail entityType="User" entityId={user.id} /> : null}
          </Box>

          <UserDetailFooter
            suspended={isSuspended}
            busy={busy}
            onSuspend={() => setSuspendOpen(true)}
            onReactivate={() => setReactivateOpen(true)}
          />
        </Box>

        <SuspendUserDialog
          open={suspendOpen}
          loading={busy}
          onConfirm={(reason) => onConfirm('suspended', reason)}
          onCancel={() => setSuspendOpen(false)}
        />
        <ReactivateUserDialog
          open={reactivateOpen}
          loading={busy}
          onConfirm={(reason) => onConfirm('active', reason)}
          onCancel={() => setReactivateOpen(false)}
        />
      </>
    );
  },
);

export const UserDetailDrawer = ({ userId, onClose }: UserDetailDrawerProps): ReactElement => {
  const { t } = useTranslation();
  const query = useAdminUserDetail(userId);

  return (
    <Drawer anchor="right" open={userId !== null} onClose={onClose}>
      {query.isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: '100vw', sm: DRAWER_WIDTH },
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}
      {query.error ? (
        <Box sx={{ width: { xs: '100vw', sm: DRAWER_WIDTH }, p: 3 }}>
          <Alert severity="error">{t('users.detail.loadError')}</Alert>
        </Box>
      ) : null}
      {query.data ? <DrawerBody key={query.data.id} user={query.data} onClose={onClose} /> : null}
    </Drawer>
  );
};
